import React, { useContext, useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Select from "react-select";

import { NotificationAlertContext } from "contexts/notificationAlertContext";
import { AuthContext } from "contexts/authContext";
import { useTour } from "@reactour/tour";
import { createDeleteMessagesExternalSteps } from "./DeleteMessagesTaskSteps";
import CustomReactDatetime from "components/Custom/CustomReactDatetime.js";
import {
  MultiValueWithCustomTooltip,
  customEllipsisMultiSelectStyles,
} from "components/Custom/CustomMultiSelect.js";

// core components
import { getList, invokeMCCDeleteMessagesTask } from "graphql/queries.js";
import { API, graphqlOperation } from "aws-amplify";
import moment from "moment";

// Hashmaps for relationships (excluding SPOKE)
let environment_client_hashmap = {};
let client_environment_hashmap = {};
let client_channel_hashmap = {};
let channel_client_hashmap = {};
let environment_channel_hashmap = {};
let channel_environment_hashmap = {};

let default_client_options = [];
let default_environment_options = [];
let default_channel_options = [];

// react-bootstrap components
import {
  Button,
  Card,
  Form,
  Container,
  Row,
  Col,
  Spinner,
} from "react-bootstrap";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";

// Import ReactTable
// import ReactTable from "components/ReactTable/ReactCustomTable";
import ReactTable from "components/ReactTable/ReactTableWithDynamicPagination";
import CustomTooltip from "components/Custom/CustomTooltip";

// Internal styles
const infoIcon = {
  fontSize: "13px",
  cursor: "pointer",
};

function CreateDeleteMessagesTask() {
  const notify = useContext(NotificationAlertContext);
  const currentUser = useContext(AuthContext);
  const location = useLocation();

  // Tour Guide
  const { setIsOpen, setCurrentStep, setSteps } = useTour();

  // Get parameters from navigation state or URL params
  const searchParams = new URLSearchParams(location.search);

  // SSO related information
  const groupName = currentUser.groupName;
  const userClientId = currentUser.clientId;
  const userClientName = currentUser.clientName;
  const isInternalUser =
    groupName === "internal" || groupName === "internal_readonly";
  const [loading, setLoading] = useState(false);

  // Client, Environment, Channel state
  const clientIdFromQuery = searchParams.get("clientId");
  const clientNameFromQuery = searchParams.get("clientName");
  const startDateFromQuery = searchParams.get("startDate");
  const environmentIdFromQuery = searchParams.get("environmentId");
  const environmentNameFromQuery = searchParams.get("environmentName");
  const channelIdFromQuery = searchParams.get("channelId");
  const channelNameFromQuery = searchParams.get("channelName");

  const [selectedClient, setSelectedClient] = useState(
    isInternalUser
      ? clientIdFromQuery
        ? {
            value: clientIdFromQuery,
            label: clientNameFromQuery,
          }
        : {
            value: "",
            label: "Select Client",
          }
      : {
          value: userClientId,
          label: userClientName,
        },
  );

  // Date filter state
  const [selectedStartDate, setSelectedStartDate] = useState(
    startDateFromQuery ? moment(startDateFromQuery) : moment(),
  );
  const [selectedEndDate, setSelectedEndDate] = useState(moment());
  const [isDateRange, setIsDateRange] = useState(false);

  const [selectedClientEnvironmentIds, setSelectedClientEnvironmentIds] =
    useState([]);
  const [selectedChannelIds, setSelectedChannelIds] = useState([]);

  const [clientOptions, setClientOptions] = useState([]);
  const [clientEnvironmentOptions, setClientEnvironmentOptions] = useState([]);
  const [channelOptions, setChannelOptions] = useState([]);

  const [changedOption, setChangedOption] = useState(null);
  const [confirmationText, setConfirmationText] = useState("");

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState([]);
  const [deleteMessagesPayload, setDeleteMessagesPayload] = useState([]);

  // Table Config Starts
  const headers = [
    { key: "environmentName", label: "Environment Name" },
    { key: "channelName", label: "Channel Name" },
  ];
  const columnMapping = {
    environmentName: "Environment Name",
    channelName: "Channel Name",
  };
  // Table Config Ends

  const loadHashes = async () => {
    setLoading(true);
    await Promise.all([
      fetchList("CHANNEL_CLIENT_HASH"),
      fetchList("CLIENT_CHANNEL_HASH"),
      fetchList("CHANNEL_ENVIRONMENT_HASH"),
      fetchList("ENVIRONMENT_CHANNEL_HASH"),
      fetchList("ENVIRONMENT_CLIENT_HASH"),
      fetchList("CLIENT_ENVIRONMENT_HASH"),
    ]);
    setLoading(false);
  };

  const fetchList = async (type) => {
    try {
      const response = await API.graphql(
        graphqlOperation(getList, {
          type: type,
          auth_client_id: userClientId,
        }),
      );
      if (response.data?.getList?.success) {
        let options = response.data.getList.body
          ? response.data.getList.body
              .map((data) => {
                const option = {
                  value: data.id,
                  label: data.name,
                  env: data.env,
                };
                if (type == "CHANNEL_CLIENT_HASH") {
                  channel_client_hashmap[data.id] = data.hash;
                  default_channel_options.push(option);
                } else if (type == "CLIENT_CHANNEL_HASH") {
                  client_channel_hashmap[data.id] = data.hash;
                  default_client_options.push(option);
                } else if (type == "CHANNEL_ENVIRONMENT_HASH") {
                  channel_environment_hashmap[data.id] = data.hash;
                } else if (type == "ENVIRONMENT_CHANNEL_HASH") {
                  environment_channel_hashmap[data.id] = data.hash;
                  default_environment_options.push(option);
                } else if (type == "ENVIRONMENT_CLIENT_HASH") {
                  environment_client_hashmap[data.id] = data.hash;
                } else if (type == "CLIENT_ENVIRONMENT_HASH") {
                  client_environment_hashmap[data.id] = data.hash;
                }
                return option;
              })
              .sort((a, b) => a.label.localeCompare(b.label))
          : [];

        if (type == "CLIENT_CHANNEL_HASH") {
          setClientOptions(options);
        } else if (type == "CHANNEL_CLIENT_HASH") {
          setChannelOptions(options);
        } else if (type == "ENVIRONMENT_CHANNEL_HASH") {
          setClientEnvironmentOptions(options);
        }
      } else {
        console.error(response.data?.getList?.message);
        notify("danger", "Could not fetch list - " + type);
      }
    } catch (error) {
      console.error(error);
      notify("danger", "Could not fetch list - " + type);
    }
  };

  useEffect(() => {
    async function fetchData() {
      await loadHashes();
      setSelectedClientEnvironmentIds(
        environmentIdFromQuery
          ? [
              {
                value: environmentIdFromQuery,
                label: environmentNameFromQuery,
              },
            ]
          : [],
      );
      setSelectedChannelIds(
        channelIdFromQuery
          ? [
              {
                value: channelIdFromQuery,
                label: channelNameFromQuery,
              },
            ]
          : [],
      );
    }
    fetchData();
  }, []);

  useEffect(() => {
    rebaseOptionsAndSelected();
  }, [
    selectedClient.value,
    selectedClientEnvironmentIds,
    selectedChannelIds,
    clientEnvironmentOptions,
    channelOptions,
  ]);

  const sortArray = (myArray) => {
    return myArray.sort((a, b) => a.label.localeCompare(b.label));
  };

  const compareArrays = (a, b) => {
    return JSON.stringify(a) === JSON.stringify(b);
  };

  const uniqueArray = (myArray) => {
    if (!myArray || myArray.length === 0) return [];
    return myArray.filter((obj, index) => {
      if (!obj) return false;
      return index === myArray.findIndex((o) => obj.value === o.value);
    });
  };

  const rebaseOptionsAndSelected = () => {
    // Rebase the environment options
    let allowable_env_channel = [];
    let allowable_env_client = [];
    if (selectedChannelIds.length > 0) {
      selectedChannelIds.forEach(function (channel) {
        allowable_env_channel = allowable_env_channel.concat(
          channel_environment_hashmap[channel.value],
        );
      });
    } else {
      allowable_env_channel = default_environment_options;
    }
    if (selectedClient.value) {
      allowable_env_client = allowable_env_client.concat(
        client_environment_hashmap[selectedClient.value],
      );
    } else {
      allowable_env_client = default_environment_options;
    }
    allowable_env_channel = uniqueArray(allowable_env_channel);
    allowable_env_client = uniqueArray(allowable_env_client);
    let newEnvironmentOptions = sortArray(
      allowable_env_channel.filter((o1) =>
        allowable_env_client.some((o2) => o1.value === o2.value),
      ),
    );
    if (
      !compareArrays(newEnvironmentOptions, sortArray(clientEnvironmentOptions))
    ) {
      setClientEnvironmentOptions(newEnvironmentOptions);
    }

    // Rebase the channel options
    let allowable_channel_client = [];
    let allowable_channel_env = [];
    if (selectedClient.value) {
      allowable_channel_client = allowable_channel_client.concat(
        client_channel_hashmap[selectedClient.value],
      );
    } else {
      allowable_channel_client = default_channel_options;
    }
    if (selectedClientEnvironmentIds.length > 0) {
      selectedClientEnvironmentIds.forEach(function (env) {
        allowable_channel_env = allowable_channel_env.concat(
          environment_channel_hashmap[env.value],
        );
      });
    } else {
      allowable_channel_env = default_channel_options;
    }
    allowable_channel_client = uniqueArray(allowable_channel_client);
    allowable_channel_env = uniqueArray(allowable_channel_env);
    let newChannelOptions = sortArray(
      allowable_channel_client.filter((o1) =>
        allowable_channel_env.some((o2) => o1.value === o2.value),
      ),
    );
    if (!compareArrays(newChannelOptions, sortArray(channelOptions))) {
      setChannelOptions(newChannelOptions);
    }

    // Rebase the selected environment
    if (changedOption == "client") {
      let newEnvironmentSelected = selectedClientEnvironmentIds.filter(
        function (cItem) {
          return clientEnvironmentOptions.find(function (aItem) {
            return cItem.value === aItem.value && cItem.label === aItem.label;
          });
        },
      );
      if (
        !compareArrays(
          sortArray(newEnvironmentSelected),
          sortArray(selectedClientEnvironmentIds),
        ) &&
        newEnvironmentSelected.length > 0
      ) {
        setSelectedClientEnvironmentIds(newEnvironmentSelected);
      }
    }

    // Rebase the selected channel
    if (changedOption == "client" || changedOption == "environment") {
      let newChannelSelected = selectedChannelIds.filter(function (cItem) {
        return channelOptions.find(function (aItem) {
          return cItem.value === aItem.value && cItem.label === aItem.label;
        });
      });
      if (
        !compareArrays(
          sortArray(newChannelSelected),
          sortArray(selectedChannelIds),
        ) &&
        newChannelSelected.length > 0
      ) {
        setSelectedChannelIds(newChannelSelected);
      }
    }
  };

  const parseChannels = (channels) => {
    return channels.map((channel) => {
      const str = channel.label;
      const lastOpen = str.lastIndexOf("(");
      const lastClose = str.lastIndexOf(")");

      // If valid parentheses exist at the end
      if (
        lastOpen !== -1 &&
        lastClose === str.length - 1 &&
        lastOpen < lastClose
      ) {
        return {
          channelName: str.slice(0, lastOpen).trim(),
          environmentName: str.slice(lastOpen + 1, lastClose).trim(),
        };
      }

      // If no environment name present
      return {
        channelName: str.trim(),
        environmentName: "N/A",
      };
    });
  };

  const handleSubmit = async () => {
    if (!selectedClient.value) {
      notify("warning", "Please select at least one client.");
      return;
    }

    if (!selectedStartDate) {
      notify("warning", "Please select a date.");
      return;
    }

    if (isDateRange) {
      if (!selectedEndDate) {
        notify("warning", "Please select an end date for the date range.");
        return;
      }

      if (moment(selectedStartDate).isAfter(selectedEndDate)) {
        notify("warning", "Start date cannot be after end date.");
        return;
      }
    }

    // Generate date range
    const dateRange = [];
    if (isDateRange && selectedEndDate) {
      const start = moment(selectedStartDate);
      const end = moment(selectedEndDate);
      for (let m = moment(start); m.isSameOrBefore(end); m.add(1, "days")) {
        dateRange.push(m.format("YYYY_MM_DD"));
      }
    } else if (selectedStartDate) {
      dateRange.push(moment(selectedStartDate).format("YYYY_MM_DD"));
    }

    // Generate prefix_data based on multi-select filters
    const envGroupedData = [];
    let allSelectedChannels = [];

    if (selectedChannelIds.length > 0) {
      // Group selected channels by their environment ('env' property)
      const channelsByEnv = selectedChannelIds.reduce((acc, channel) => {
        const envId = channel.env;
        if (!acc[envId]) {
          acc[envId] = [];
        }
        acc[envId].push(channel);
        return acc;
      }, {});

      // Create prefix_data for each environment group
      Object.entries(channelsByEnv).forEach(([envId, channels]) => {
        const prefixes = [];
        channels.forEach((channel) => {
          dateRange.forEach((date) => {
            prefixes.push({
              channel_id: channel.value,
              date_rollup: date,
            });
          });
        });
        envGroupedData.push({
          environment_id: envId,
          prefixes: prefixes,
        });
      });
      allSelectedChannels = parseChannels(selectedChannelIds);
    } else if (
      selectedClientEnvironmentIds.length > 0 &&
      selectedChannelIds.length === 0
    ) {
      // Only environments selected - use all channels for those environments
      selectedClientEnvironmentIds.forEach((env) => {
        const channelsForEnv = channelOptions.filter(
          (ch) => ch.env === env.value,
        );
        const prefixes = [];
        channelsForEnv.forEach((channel) => {
          dateRange.forEach((date) => {
            prefixes.push({
              channel_id: channel.value,
              date_rollup: date,
            });
          });
        });
        envGroupedData.push({
          environment_id: env.value,
          prefixes: prefixes,
        });
        allSelectedChannels = allSelectedChannels.concat(
          parseChannels(channelsForEnv),
        );
      });
    } else if (
      selectedClientEnvironmentIds.length === 0 &&
      selectedChannelIds.length === 0
    ) {
      // No specific filters - get all environments and channels for selected clients
      clientEnvironmentOptions.forEach((env) => {
        const channelsForEnv = channelOptions.filter(
          (ch) => ch.env === env.value,
        );
        const prefixes = [];
        channelsForEnv.forEach((channel) => {
          dateRange.forEach((date) => {
            prefixes.push({
              channel_id: channel.value,
              date_rollup: date,
            });
          });
        });
        envGroupedData.push({
          environment_id: env.value,
          prefixes: prefixes,
        });
        allSelectedChannels = allSelectedChannels.concat(
          parseChannels(channelsForEnv),
        );
      });
    }

    if (envGroupedData.length === 0) {
      notify("warning", "No data to process for deletion.");
      return;
    }

    setDeleteMessagesPayload(envGroupedData);
    setModalData(allSelectedChannels);
    setShowModal(true);
  };

  const reset = () => {
    setSelectedClient(
      isInternalUser
        ? {
            value: "",
            label: "Select Client",
          }
        : {
            value: userClientId,
            label: userClientName,
          },
    );
    setSelectedClientEnvironmentIds([]);
    setSelectedChannelIds([]);
    setSelectedStartDate(moment());
    setSelectedEndDate(moment());
    setClientEnvironmentOptions([]);
    setChannelOptions([]);
  };

  // Create delete messages task
  const createDeleteMessagesTask = async () => {
    try {
      setLoading(true);
      // Create the invokeMCCDeletMessagesTask payload
      const taskPayload = {
        client_id: selectedClient.value,
        delete_messages_payload: deleteMessagesPayload,
      };

      const response = await API.graphql(
        graphqlOperation(invokeMCCDeleteMessagesTask, taskPayload),
      );

      const result = response.data.invokeMCCDeleteMessagesTask;

      if (result?.success && result?.body) {
        const { cloud_task_ids, skipped_environments } = result.body;

        // Show success notification if there are cloud_task_ids
        if (cloud_task_ids && cloud_task_ids.length > 0) {
          const taskIdsStr = cloud_task_ids.join(", ");
          notify(
            "success",
            `Delete messages task created successfully. Task IDs: ${taskIdsStr}`,
            -1,
          );
        }

        // Show danger notification if there are skipped environments
        if (skipped_environments && skipped_environments.length > 0) {
          const skippedEnvsNames = skipped_environments
            .map((env) => env.environment_name)
            .join(", ");
          notify(
            "danger",
            `Your deletion request was not created. Please retry the request after ${skippedEnvsNames} pending tasks have been completed.`,
            -1,
          );
        }

        // If no tasks were created and no environments were skipped, show a generic warning message
        if (
          (!cloud_task_ids || cloud_task_ids.length === 0) &&
          (!skipped_environments || skipped_environments.length === 0)
        ) {
          notify(
            "warning",
            "No delete messages tasks were created nor any environments were skipped.",
          );
        }
      } else {
        throw new Error(
          result?.message || "Failed to create delete messages task",
        );
      }
    } catch (error) {
      console.error("Error creating delete messages task:", error);
      notify("danger");
    } finally {
      setShowModal(false);
      setLoading(false);
    }
  };

  if (loading) return <Spinner animation="grow" />;

  return (
    <>
      <Container fluid>
        <Row>
          <Col>
            <h2>Delete Messages</h2>
          </Col>
          {currentUser.groupName === "external" && (
            <Col className="my-auto text-right">
              <Button
                variant="secondary"
                className="btn-outline"
                onClick={() => {
                  setSteps(createDeleteMessagesExternalSteps);
                  setCurrentStep(0);
                  setIsOpen(true);
                }}
              >
                <i className="fas fa-desktop"></i> View Our Tour Guide
              </Button>
            </Col>
          )}
        </Row>

        <Row>
          <Col md="12">
            <Card>
              <Card.Header>
                <Card.Title as="h4">Delete Messages Filter</Card.Title>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md="4">
                    <Form.Group id="client-select">
                      {!isInternalUser && (
                        <>
                          <label>Clients Filter (Required)</label>
                          <Form.Control
                            type="text"
                            disabled={true}
                            value={selectedClient.label}
                          ></Form.Control>
                        </>
                      )}
                      {isInternalUser && (
                        <>
                          <label>Clients Filter (Required)</label>
                          <Select
                            id="client"
                            className="react-select primary"
                            classNamePrefix="react-select"
                            name="clientList"
                            value={selectedClient}
                            onChange={(event) => {
                              setSelectedClient({
                                value: event.value,
                                label: event.label,
                              });
                              setChangedOption("client");
                              setSelectedClientEnvironmentIds([]);
                              setSelectedChannelIds([]);
                            }}
                            options={clientOptions}
                            placeholder="Select Client"
                          />
                        </>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md="4">
                    <Form.Group id="client-environment-filter-group">
                      <label>Client Environment Filter</label>
                      <Select
                        id="client-environment-select"
                        className="react-select primary"
                        classNamePrefix="react-select"
                        name="clientEnvironmentFilter"
                        closeMenuOnSelect={false}
                        isMulti
                        isDisabled={!selectedClient.value}
                        styles={customEllipsisMultiSelectStyles}
                        components={{ MultiValue: MultiValueWithCustomTooltip }}
                        value={selectedClientEnvironmentIds}
                        onChange={(clientEnvironments) => {
                          setSelectedClientEnvironmentIds(
                            clientEnvironments ? clientEnvironments : [],
                          );
                          setChangedOption("environment");
                        }}
                        options={clientEnvironmentOptions}
                        isClearable={false}
                        placeholder="None Selected"
                      />
                    </Form.Group>
                  </Col>
                  <Col md="4">
                    <Form.Group id="channel-filter-group">
                      <label>Channel Filter</label>
                      <Select
                        id="channel-select"
                        className="react-select primary"
                        classNamePrefix="react-select"
                        name="channelFilter"
                        closeMenuOnSelect={false}
                        isMulti
                        isDisabled={!selectedClient.value}
                        styles={customEllipsisMultiSelectStyles}
                        components={{ MultiValue: MultiValueWithCustomTooltip }}
                        value={selectedChannelIds}
                        onChange={(channels) => {
                          setSelectedChannelIds(channels ? channels : []);
                          setChangedOption("channel");
                        }}
                        options={channelOptions}
                        isClearable={false}
                        placeholder="None Selected"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="align-items-center">
                  <Col md="4" id="date-range-toggle">
                    <Form.Check>
                      <Form.Check.Label>
                        <Form.Check.Input
                          type="checkbox"
                          checked={isDateRange}
                          onChange={(event) => {
                            setIsDateRange(event.target.checked);
                            if (!event.target.checked) {
                              setSelectedEndDate(moment());
                            }
                          }}
                        ></Form.Check.Input>
                        <span className="form-check-sign"></span>
                        Date Range
                      </Form.Check.Label>
                    </Form.Check>
                  </Col>
                  <Col md="4" id="delete-message-date-picker">
                    <Form.Group>
                      <label>
                        {isDateRange ? "Start Date" : "Select Date"}
                        <CustomTooltip
                          id="delete-message-start-date-tooltip"
                          value={
                            isDateRange
                              ? "Message received between the start date and end date will be deleted."
                              : "Messages received on this date will be deleted."
                          }
                          placement="left"
                        >
                          <i className="fa fa-info-circle" style={infoIcon}></i>
                        </CustomTooltip>
                      </label>
                      <CustomReactDatetime
                        inputProps={{
                          className: "form-control",
                          placeholder: "Not Selected",
                          value: selectedStartDate
                            ? moment(selectedStartDate).format("MM/DD/YYYY")
                            : "",
                        }}
                        timeFormat={false}
                        value={selectedStartDate}
                        onChange={(value) => {
                          setSelectedStartDate(value);
                        }}
                      ></CustomReactDatetime>
                    </Form.Group>
                  </Col>
                  {isDateRange && (
                    <Col md="4">
                      <Form.Group>
                        <label>End Date</label>
                        <CustomReactDatetime
                          inputProps={{
                            className: "form-control",
                            placeholder: "Not Selected",
                            value: selectedEndDate
                              ? moment(selectedEndDate).format("MM/DD/YYYY")
                              : "",
                          }}
                          timeFormat={false}
                          value={selectedEndDate}
                          onChange={(value) => {
                            setSelectedEndDate(value);
                          }}
                        ></CustomReactDatetime>
                      </Form.Group>
                    </Col>
                  )}
                </Row>

                <Row className="mt-3">
                  <Col md="12">
                    <Button
                      id="delete-messages-submit-button"
                      className="btn-fill"
                      color="primary"
                      type="button"
                      onClick={handleSubmit}
                      disabled={!selectedClient.value}
                    >
                      Submit
                    </Button>
                    <Button
                      className="btn-outline ml-2"
                      variant="default"
                      type="button"
                      onClick={reset}
                    >
                      Reset
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Modal for displaying selected channels */}
      <Modal
        toggle={() => setShowModal(!showModal)}
        size="xl"
        isOpen={showModal}
        scrollable={true}
      >
        <ModalHeader>
          {isDateRange ? (
            <>
              Confirm the deletions for the channels below for the date range{" "}
              {selectedStartDate.format("MM/DD/YYYY")} to{" "}
              {selectedEndDate.format("MM/DD/YYYY")}
            </>
          ) : (
            <>
              Confirm the deletions for the channels below for the date{" "}
              {selectedStartDate.format("MM/DD/YYYY")}
            </>
          )}
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md="12">
              <ReactTable
                data={modalData}
                totalCount={modalData.length}
                makePaginationStatic={true}
                hidePagination={true}
                numberOfRowsData={[]}
                columnMapping={columnMapping}
                tableName="Channels"
                CSVFileName={"Delete_Messages_Channels_Export"}
                CSVHeaders={headers}
                columns={[
                  {
                    Header: "Environment Name",
                    accessor: "environmentName",
                    filterable: false,
                    showTooltip: true,
                    sortable: true,
                  },
                  {
                    Header: "Channel Name",
                    accessor: "channelName",
                    filterable: false,
                    showTooltip: true,
                    sortable: true,
                  },
                ]}
              />
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Form.Group className="w-100 mb-3">
            <Form.Label>
              To confirm deletion, enter the message "delete messages" in the
              text input field.
            </Form.Label>
            <Form.Control
              id="confirmation-input"
              placeholder="Type 'delete messages' to confirm"
              type="text"
              className="form-control"
              name="confirmationText"
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
            />
          </Form.Group>
          <Button
            variant="secondary"
            onClick={() => {
              setShowModal(false);
              setConfirmationText("");
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={createDeleteMessagesTask}
            disabled={confirmationText !== "delete messages"}
          >
            Confirm
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default CreateDeleteMessagesTask;
